import { useState } from 'react';
import {
  useAdminProducts,
  useAdminUsers,
  useGrantAccess,
  useRevokeAccess,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate } from '../../lib/format';
import { Badge, Button, ConfirmDialog, DataTable, SkeletonList, useToast } from '../../ui';
import { GrantAccessModal } from './GrantAccessModal';

/** Ime iz profila; korisnik bez profila nema nijedno od dva polja. */
function fullName(row: { firstName: string | null; lastName: string | null }): string | null {
  const name = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
  return name.length > 0 ? name : null;
}

export function AdminUsers() {
  const { t, locale } = useTranslation();
  const query = useAdminUsers();
  const products = useAdminProducts();
  const grant = useGrantAccess();
  const revoke = useRevokeAccess();
  const { toast } = useToast();

  /*
    Odobrenje i oduzimanje su jedine odluke na ovom ekranu, pa se drži samo koji
    je klijent u pitanju. Ime se pamti uz njega — dijalog ne smije biti anoniman
    ni nakon što se popis u pozadini osvježi.
  */
  const [zaOdobrenje, setZaOdobrenje] = useState<{ id: string; label: string } | null>(null);
  const [zaOduzimanje, setZaOduzimanje] = useState<{ id: string; label: string } | null>(null);

  return (
    <>
      <PageHeader eyebrow={t('shell.admin')} title={t('admin.users')} />
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={5} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('empty.noUsers') }}
      >
        {(data) => (
          <DataTable
            caption={t('admin.users')}
            rows={data.items}
            rowKey={(r) => r.id}
            columns={[
              {
                key: 'name',
                header: t('auth.firstName'),
                render: (r) => (
                  <span className="flex items-center gap-2">
                    {/*
                      Korisnik ne mora imati profil (registracija ga stvara, ali
                      seed i stariji zapisi ne). Bez imena stoji crtica, ne prazno
                      polje — prazan redak izgleda kao greška u učitavanju.
                    */}
                    <span>{fullName(r) ?? '—'}</span>
                    {r.deletedAt && <Badge tone="neutral">{t('admin.deleted')}</Badge>}
                  </span>
                ),
              },
              { key: 'email', header: t('auth.email'), render: (r) => r.email, hideOnMobile: true },
              {
                key: 'role',
                header: t('admin.role'),
                render: (r) => (
                  <Badge tone={r.role === 'admin' ? 'danger' : r.role === 'trainer' ? 'accent' : 'neutral'}>
                    {r.role}
                  </Badge>
                ),
              },
              {
                key: 'created',
                header: t('admin.createdAt'),
                render: (r) => formatDate(r.createdAt, locale),
                align: 'right',
                hideOnMobile: true,
              },
              {
                key: 'lastSeen',
                header: t('admin.lastSeen'),
                render: (r) => (r.lastSeenAt ? formatDate(r.lastSeenAt, locale) : '—'),
                align: 'right',
                hideOnMobile: true,
              },
              {
                key: 'access',
                header: t('admin.access'),
                /*
                  Samo klijenti. Trener i admin nemaju entitlemente i nikad ih
                  neće imati, pa bi im „Nema" ovdje bila netočna optužba.
                */
                render: (r) =>
                  r.role !== 'client' ? (
                    <span className="text-ink-muted">—</span>
                  ) : (
                    <span className="flex items-center justify-end gap-2">
                      <Badge tone={r.hasAccess ? 'success' : 'neutral'}>
                        {r.hasAccess ? t('admin.accessActive') : t('admin.accessNone')}
                      </Badge>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          r.hasAccess
                            ? setZaOduzimanje({ id: r.id, label: fullName(r) ?? r.email })
                            : setZaOdobrenje({ id: r.id, label: fullName(r) ?? r.email })
                        }
                      >
                        {r.hasAccess ? t('admin.revokeAccess') : t('admin.grantAccess')}
                      </Button>
                    </span>
                  ),
                align: 'right',
              },
            ]}
          />
        )}
      </QueryBoundary>

      {zaOdobrenje && (
        <GrantAccessModal
          open
          onClose={() => setZaOdobrenje(null)}
          clientLabel={zaOdobrenje.label}
          products={(products.data?.items ?? []).map((p) => ({ id: p.id, name: p.nameHr }))}
          loading={grant.isPending}
          onSubmit={(values) => {
            grant.mutate(
              { userId: zaOdobrenje.id, ...values },
              {
                onSuccess: () => {
                  toast(t('admin.grantAccess'), 'success');
                  setZaOdobrenje(null);
                },
                onError: () => toast(t('error.generic'), 'danger'),
              },
            );
          }}
        />
      )}

      {zaOduzimanje && (
        <ConfirmDialog
          open
          title={t('admin.revokeTitle')}
          question={`${zaOduzimanje.label} — ${t('admin.revokeHelp')}`}
          confirmLabel={t('admin.revokeAccess')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          tone="danger"
          loading={revoke.isPending}
          onClose={() => setZaOduzimanje(null)}
          onConfirm={() =>
            revoke.mutate(
              { userId: zaOduzimanje.id, note: 'Oduzeto iz admina' },
              {
                onSuccess: () => {
                  toast(t('admin.revokeAccess'), 'success');
                  setZaOduzimanje(null);
                },
                onError: () => toast(t('error.generic'), 'danger'),
              },
            )
          }
        />
      )}
    </>
  );
}
